import _ from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles } from '@material-ui/core';

import Transition from '../../components/Dialogs/Transition';
import {
	search as searchSalesOverrides,
	remove as removeSalesOverride,
} from '../../modules/reports/salesOverride';

const styles = () => ({
	dialogPaper: {
		maxWidth: '100%',
		width: 980,
	},
	dialogPadding: {
		padding: '10px 25px 20px 25px',
	},
	reasonCell: {
		maxWidth: 260,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	},
	hiddenRow: {
		opacity: 0.55,
	},
	removedInfo: {
		fontSize: 12,
		color: '#999',
	},
	empty: {
		padding: '30px 0',
		textAlign: 'center',
		color: '#888',
	},
	errorText: {
		color: '#c62828',
		fontSize: '0.85rem',
		marginTop: 8,
	},
});

/**
 * View (and remove) the manual sales overrides behind the Station Daily Sales report.
 * Removed overrides stay in the audit trail and show behind the "Show removed" toggle.
 */
class SalesOverridesDialog extends React.Component {
	constructor(props) {
		super(props);
		this.state = {
			overrides: null,
			userMap: {},
			includeHidden: false,
			isLoading: false,
			confirmRemoveId: null,
			removingId: null,
			error: null,
		};
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.props.open && !prevProps.open) {
			this.setState(
				{ overrides: null, includeHidden: false, confirmRemoveId: null, error: null },
				this.load
			);
			return;
		}
		if (this.props.open && prevState.includeHidden !== this.state.includeHidden) {
			this.load();
		}
	}

	load = () => {
		this.setState({ isLoading: true, error: null, confirmRemoveId: null });
		searchSalesOverrides({
			includeHidden: this.state.includeHidden,
			callback: (err, result) => {
				if (err) {
					const errors = _.isArray(err) ? err : [err];
					return this.setState({
						isLoading: false,
						error: errors.map((e) => (e && e.message) || String(e)).join(', '),
					});
				}
				const overrides = _.get(result, ['collections', 'salesOverrideCollection'], []);
				const users = _.get(result, ['collections', 'userCollection'], []);
				this.setState({
					isLoading: false,
					overrides,
					userMap: _.keyBy(users, 'userId'),
				});
			},
		});
	};

	handleRemove = (salesOverrideId) => {
		this.setState({ removingId: salesOverrideId, error: null });
		removeSalesOverride({
			salesOverrideId,
			callback: (err) => {
				if (err) {
					const errors = _.isArray(err) ? err : [err];
					return this.setState({
						removingId: null,
						error: errors.map((e) => (e && e.message) || String(e)).join(', '),
					});
				}
				this.setState({ removingId: null, confirmRemoveId: null });
				this.load();
				this.props.onChanged();
			},
		});
	};

	userName = (userId) => {
		const user = this.state.userMap[userId];
		if (!user) {
			return userId ? `User #${userId}` : '-';
		}
		return `${user.firstName} ${user.lastName}`;
	};

	stationDisplay = (stationId) => {
		const station = _.get(this.props.stationMap, [stationId]);
		if (!station) {
			return `Station #${stationId}`;
		}
		return `${station.number} - ${station.name}`;
	};

	render() {
		const { classes, open, onClose, canManage } = this.props;
		const { overrides, includeHidden, isLoading, confirmRemoveId, removingId, error } = this.state;

		return (
			<Dialog
				classes={{ paper: classes.dialogPaper }}
				open={open}
				TransitionComponent={Transition}
				onClose={onClose}
			>
				<div className={classes.dialogPadding}>
					<DialogTitle>Sales Overrides</DialogTitle>
					<DialogContent>
						<FormControlLabel
							control={
								<Switch
									checked={includeHidden}
									color="primary"
									onChange={(e) => this.setState({ includeHidden: e.target.checked })}
								/>
							}
							label="Show removed"
						/>

						{isLoading && !overrides && (
							<div className={classes.empty}>
								<CircularProgress size={28} />
							</div>
						)}

						{overrides && _.isEmpty(overrides) && (
							<div className={classes.empty}>No sales overrides yet.</div>
						)}

						{overrides && !_.isEmpty(overrides) && (
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>Station</TableCell>
										<TableCell>Tank</TableCell>
										<TableCell>Sales Date</TableCell>
										<TableCell align="right">Volume</TableCell>
										<TableCell>Reason</TableCell>
										<TableCell>Added By</TableCell>
										<TableCell />
									</TableRow>
								</TableHead>
								<TableBody>
									{overrides.map((override) => {
										const {
											salesOverrideId,
											stationId,
											tankNumber,
											salesDate,
											volume,
											reason,
											createdByUserId,
											hiddenByUserId,
											isHidden,
											dateCreated,
											dateHidden,
										} = override;

										return (
											<TableRow
												key={salesOverrideId}
												className={isHidden ? classes.hiddenRow : undefined}
											>
												<TableCell>{this.stationDisplay(stationId)}</TableCell>
												<TableCell>{_.isNumber(tankNumber) ? `#${tankNumber}` : 'All'}</TableCell>
												<TableCell>{moment(salesDate).format('MM/DD/YYYY')}</TableCell>
												<TableCell align="right">{volume}</TableCell>
												<TableCell className={classes.reasonCell}>{reason || '-'}</TableCell>
												<TableCell>
													{this.userName(createdByUserId)}
													<div className={classes.removedInfo}>
														{moment(dateCreated).format('MM/DD/YYYY')}
													</div>
												</TableCell>
												<TableCell align="right">
													{isHidden && (
														<span className={classes.removedInfo}>
															Removed by {this.userName(hiddenByUserId)}
															{dateHidden ? ` on ${moment(dateHidden).format('MM/DD/YYYY')}` : ''}
														</span>
													)}
													{!isHidden && canManage && confirmRemoveId !== salesOverrideId && (
														<Button
															size="small"
															onClick={() => this.setState({ confirmRemoveId: salesOverrideId })}
														>
															Remove
														</Button>
													)}
													{!isHidden && canManage && confirmRemoveId === salesOverrideId && (
														<Button
															size="small"
															color="secondary"
															variant="contained"
															disabled={removingId === salesOverrideId}
															onClick={() => this.handleRemove(salesOverrideId)}
														>
															{removingId === salesOverrideId ? (
																<CircularProgress size={16} />
															) : (
																'Confirm Remove'
															)}
														</Button>
													)}
												</TableCell>
											</TableRow>
										);
									})}
								</TableBody>
							</Table>
						)}

						{error && <div className={classes.errorText}>{error}</div>}
					</DialogContent>
					<DialogActions>
						<Button onClick={onClose}>Close</Button>
					</DialogActions>
				</div>
			</Dialog>
		);
	}
}

SalesOverridesDialog.propTypes = {
	classes: PropTypes.object.isRequired,
	open: PropTypes.bool.isRequired,
	stationMap: PropTypes.object,
	canManage: PropTypes.bool,
	onClose: PropTypes.func.isRequired,
	onChanged: PropTypes.func.isRequired,
};

export default withStyles(styles)(SalesOverridesDialog);
